'use client';

import { c as _c } from "react/compiler-runtime";
import { FlexContainer, FormControl, FormDescription, FormItem, FormLabel, FormMessage, ToggleGroup, ToggleGroupItem } from '../../components';
import type { ReactNode } from 'react';
import type { FieldValues } from 'react-hook-form';
import type { BaseComponentProps, BaseFieldProps } from './types';

/** Option type for ToggleGroup that supports ReactNode labels for rich content */
export interface ToggleGroupOption {
  label: ReactNode;
  value: string;
  /** Greys out and blocks selection of this single option (the group can still be used). */
  disabled?: boolean;
}
type ToggleGroupSharedProps = BaseComponentProps<typeof ToggleGroup> & {
  options?: ToggleGroupOption[];
  clearable?: boolean;
  classNames?: {
    base?: string;
    item?: string;
  };
  descriptionPosition?: 'top' | 'bottom';
};
export type ToggleGroupComponentProps = ToggleGroupSharedProps & ({
  type?: 'single';
  defaultValue?: string;
} | {
  type: 'multiple';
  defaultValue?: string[];
});

// Update main props interface to extend BaseFieldProps
export interface ToggleGroupFieldProps<TFieldValues extends FieldValues> extends BaseFieldProps<TFieldValues> {
  componentProps: ToggleGroupComponentProps;
}
const getAriaLabel = (label: React.ReactNode) => {
  if (typeof label === 'string') {
    return label;
  }
  return label?.toString();
};
export function ToggleGroupField(t0) {
  const $ = _c(62);
  const {
    field,
    fieldState,
    fieldConfig,
    componentProps,
    labelSize
  } = t0;
  let classNames;
  let componentDisabled;
  let defaultValue;
  let id;
  let options;
  let restComponentProps;
  let t1;
  let t2;
  let t3;
  if ($[0] !== componentProps) {
    ({
      id,
      options,
      type: t1,
      disabled: componentDisabled,
      clearable: t2,
      classNames,
      descriptionPosition: t3,
      defaultValue,
      ...restComponentProps
    } = componentProps);
    $[0] = componentProps;
    $[1] = classNames;
    $[2] = componentDisabled;
    $[3] = defaultValue;
    $[4] = id;
    $[5] = options;
    $[6] = restComponentProps;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    classNames = $[1];
    componentDisabled = $[2];
    defaultValue = $[3];
    id = $[4];
    options = $[5];
    restComponentProps = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
  }
  const componentType = t1 === undefined ? "single" : t1;
  const clearable = t2 === undefined ? true : t2;
  const descriptionPosition = t3 === undefined ? "bottom" : t3;
  const isDisabled = field.disabled || componentDisabled;
  const fieldId = id || String(fieldConfig.name);
  const descriptionId = fieldConfig.description ? `${fieldId}-description` : undefined;
  const errorId = fieldState.error?.message ? `${fieldId}-error` : undefined;
  let t4;
  if ($[10] !== descriptionId || $[11] !== errorId) {
    t4 = [descriptionId, errorId].filter(Boolean).join(" ") || undefined;
    $[10] = descriptionId;
    $[11] = errorId;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const describedBy = t4;
  let t5;
  if ($[13] !== classNames?.item || $[14] !== isDisabled || $[15] !== options) {
    t5 = () => options?.map(option => <ToggleGroupItem className={classNames?.item} aria-label={getAriaLabel(option.label)} disabled={isDisabled || option.disabled} key={option.value} value={option.value}>{option.label}</ToggleGroupItem>);
    $[13] = classNames?.item;
    $[14] = isDisabled;
    $[15] = options;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const renderToggleItems = t5;
  let t6;
  if ($[17] !== fieldConfig.label) {
    t6 = getAriaLabel(fieldConfig.label);
    $[17] = fieldConfig.label;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const t7 = !!fieldState.error;
  const t8 = classNames?.base;
  let t9;
  if ($[19] !== describedBy || $[20] !== fieldId || $[21] !== isDisabled || $[22] !== restComponentProps || $[23] !== t6 || $[24] !== t7 || $[25] !== t8) {
    t9 = {
      ...restComponentProps,
      disabled: isDisabled,
      id: fieldId,
      role: "group",
      "aria-label": t6,
      "aria-describedby": describedBy,
      "aria-invalid": t7,
      className: t8
    };
    $[19] = describedBy;
    $[20] = fieldId;
    $[21] = isDisabled;
    $[22] = restComponentProps;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  const toggleGroupBaseProps = t9;
  let t10;
  if ($[27] !== clearable || $[28] !== field) {
    t10 = value => {
      if (value.length > 0 || clearable) {
        field.onChange(value || undefined);
      }
    };
    $[27] = clearable;
    $[28] = field;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const onValueChange = t10;
  const t11 = `${fieldId}-label`;
  let t12;
  if ($[30] !== fieldConfig.label || $[31] !== fieldConfig.required || $[32] !== fieldId || $[33] !== labelSize || $[34] !== t11) {
    t12 = <FormLabel htmlFor={fieldId} id={t11} required={fieldConfig.required} size={labelSize} weight="medium">{fieldConfig.label}</FormLabel>;
    $[30] = fieldConfig.label;
    $[31] = fieldConfig.required;
    $[32] = fieldId;
    $[33] = labelSize;
    $[34] = t11;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== descriptionId || $[37] !== descriptionPosition || $[38] !== fieldConfig.description) {
    t13 = fieldConfig.description && descriptionPosition === "top" && <FormDescription id={descriptionId}>{fieldConfig.description}</FormDescription>;
    $[36] = descriptionId;
    $[37] = descriptionPosition;
    $[38] = fieldConfig.description;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== t12 || $[41] !== t13) {
    t14 = <FlexContainer className="flex flex-col gap-0.5">{t12}{t13}</FlexContainer>;
    $[40] = t12;
    $[41] = t13;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] !== componentType || $[44] !== defaultValue || $[45] !== field.value || $[46] !== onValueChange || $[47] !== renderToggleItems || $[48] !== toggleGroupBaseProps) {
    t15 = <FormControl className="overflow-x-auto sm:overflow-x-visible">{componentType === "multiple" ? <ToggleGroup {...toggleGroupBaseProps} defaultValue={defaultValue as string[] | undefined} onValueChange={onValueChange} type="multiple" value={field.value as string[]}>{renderToggleItems()}</ToggleGroup> : <ToggleGroup {...toggleGroupBaseProps} defaultValue={defaultValue as string | undefined} onValueChange={onValueChange} type="single" value={field.value as string}>{renderToggleItems()}</ToggleGroup>}</FormControl>;
    $[43] = componentType;
    $[44] = defaultValue;
    $[45] = field.value;
    $[46] = onValueChange;
    $[47] = renderToggleItems;
    $[48] = toggleGroupBaseProps;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  let t16;
  if ($[50] !== descriptionId || $[51] !== descriptionPosition || $[52] !== fieldConfig.description) {
    t16 = fieldConfig.description && descriptionPosition === "bottom" && <FormDescription id={descriptionId}>{fieldConfig.description}</FormDescription>;
    $[50] = descriptionId;
    $[51] = descriptionPosition;
    $[52] = fieldConfig.description;
    $[53] = t16;
  } else {
    t16 = $[53];
  }
  let t17;
  if ($[54] !== errorId || $[55] !== fieldState.error) {
    t17 = fieldState.error?.message && <FormMessage id={errorId}>{fieldState.error.message}</FormMessage>;
    $[54] = errorId;
    $[55] = fieldState.error;
    $[56] = t17;
  } else {
    t17 = $[56];
  }
  let t18;
  if ($[57] !== t14 || $[58] !== t15 || $[59] !== t16 || $[60] !== t17) {
    t18 = <FormItem className="flex-1">{t14}{t15}{t16}{t17}</FormItem>;
    $[57] = t14;
    $[58] = t15;
    $[59] = t16;
    $[60] = t17;
    $[61] = t18;
  } else {
    t18 = $[61];
  }
  return t18;
}